import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Shield, UserPlus, Edit, Trash2, Save, X, Plus, 
  CheckSquare, Square, Lock, Unlock, Eye, EyeOff, RotateCcw, Key
} from 'lucide-react';
import { userCategoryService } from '../services/userCategoryService';
import { userManagementService } from '../services/userManagementService';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { parseErrorMessage } from '../utils/helpers';

// Lista de permisos del sistema
const PERMISSIONS_LIST = [
  // Dashboard
  { key: 'dashboard.view', label: 'Ver Dashboard', category: 'Dashboard' },

  // Contactos
  { key: 'contacts.view', label: 'Ver Contactos', category: 'Contactos' },
  { key: 'contacts.create', label: 'Crear Contactos', category: 'Contactos' },
  { key: 'contacts.edit', label: 'Editar Contactos', category: 'Contactos' },
  { key: 'contacts.delete', label: 'Eliminar Contactos', category: 'Contactos' },

  // Empresas
  { key: 'companies.view', label: 'Ver Empresas', category: 'Empresas' },
  { key: 'companies.create', label: 'Crear Empresas', category: 'Empresas' },
  { key: 'companies.edit', label: 'Editar Empresas', category: 'Empresas' },
  { key: 'companies.delete', label: 'Eliminar Empresas', category: 'Empresas' },

  // Producto
  { key: 'products.view', label: 'Ver productos', category: 'Producto' },
  { key: 'products.create', label: 'Crear Productos', category: 'Producto' },
  { key: 'products.edit', label: 'Editar Productos', category: 'Producto' },
  { key: 'products.delete', label: 'Eliminar Productos', category: 'Producto' },
  { key: 'products.import_stock', label: 'Importar Stock', category: 'Producto' },

  // Cotizaciones
  { key: 'proposals.view', label: 'Ver Cotizaciones', category: 'Cotizaciones' },
  { key: 'proposals.create', label: 'Crear Cotizaciones', category: 'Cotizaciones' },
  { key: 'proposals.edit', label: 'Editar Cotizaciones', category: 'Cotizaciones' },
  { key: 'proposals.delete', label: 'Eliminar Cotizaciones', category: 'Cotizaciones' },
  { key: 'proposals.approve', label: 'Aprobar Cotizaciones', category: 'Cotizaciones' },
  { key: 'proposals.send', label: 'Enviar Cotizaciones', category: 'Cotizaciones' },
  { key: 'proposals.view_indicators', label: 'Ver Indicadores', category: 'Cotizaciones' },

  // Órdenes Completas
  { key: 'orders.view', label: 'Ver Órdenes Completas', category: 'Órdenes' },
  { key: 'orders.create', label: 'Crear Órdenes Completas', category: 'Órdenes' },
  { key: 'orders.edit', label: 'Editar Órdenes Completas', category: 'Órdenes' },
  { key: 'orders.delete', label: 'Eliminar Órdenes Completas', category: 'Órdenes' },

  // PO Individuales
  { key: 'purchase_orders.view', label: 'Ver PO Individuales', category: 'PO Individuales' },
  { key: 'purchase_orders.create', label: 'Crear PO Individuales', category: 'PO Individuales' },
  { key: 'purchase_orders.edit', label: 'Editar PO Individuales', category: 'PO Individuales' },
  { key: 'purchase_orders.delete', label: 'Eliminar PO Individuales', category: 'PO Individuales' },

  // Reportes
  { key: 'reports.view', label: 'Ver Reportes', category: 'Reportes' },
  { key: 'reports.billing', label: 'Ver Reporte de Facturación', category: 'Reportes' },
  { key: 'reports.billing.margins', label: 'Ver Márgenes y Costos', category: 'Reportes' },
  { key: 'reports.export', label: 'Exportar Reportes', category: 'Reportes' },
  { key: 'operations.schedules.edit', label: 'Editar Horarios', category: 'Operaciones' },
  { key: 'operations.schedules.shifts', label: 'Gestionar Turnos', category: 'Operaciones' },
  { key: 'operations.schedules.holidays', label: 'Gestionar Feriados', category: 'Operaciones' },
  
  // Tareas
  { key: 'tasks.view', label: 'Ver Tareas', category: 'Tareas' },
  { key: 'tasks.create', label: 'Crear Tareas', category: 'Tareas' },
  { key: 'tasks.edit', label: 'Editar Tareas', category: 'Tareas' },
  { key: 'tasks.delete', label: 'Eliminar Tareas', category: 'Tareas' },
  { key: 'tasks.assign', label: 'Asignar Tareas', category: 'Tareas' },
  { key: 'tasks.complete', label: 'Completar Tareas', category: 'Tareas' },
  
  // Noticias
  { key: 'news.view', label: 'Ver Noticias', category: 'Noticias' },
  { key: 'news.create', label: 'Crear Noticias', category: 'Noticias' },
  { key: 'news.edit', label: 'Editar Noticias', category: 'Noticias' },
  { key: 'news.delete', label: 'Eliminar Noticias', category: 'Noticias' },
  { key: 'news.publish', label: 'Publicar Noticias', category: 'Noticias' },
  
  // Auditoría
  { key: 'audit.view', label: 'Ver Auditoría', category: 'Auditoría' },
  { key: 'audit.time_usage', label: 'Ver Tiempo de Uso', category: 'Auditoría' },
  { key: 'audit.time_usage.all_users', label: 'Ver Tiempo de Uso de Todos los Usuarios', category: 'Auditoría' },
  { key: 'audit.logs', label: 'Ver Logs de Auditoría', category: 'Auditoría' },
  { key: 'audit.logs.all_users', label: 'Ver Logs de Auditoría de Todos los Usuarios', category: 'Auditoría' },
  { key: 'audit.export', label: 'Exportar Reportes de Auditoría', category: 'Auditoría' },
  
  // Reportes
  { key: 'reports.view', label: 'Ver Reportes', category: 'Reportes' },
  { key: 'reports.billing', label: 'Ver Reporte de Facturación', category: 'Reportes' },
  { key: 'reports.billing.margins', label: 'Ver Márgenes y Costos en Reporte de Facturación', category: 'Reportes' },
  { key: 'reports.stock', label: 'Ver Reporte de Stock', category: 'Reportes' },
  { key: 'reports.export', label: 'Exportar Reportes', category: 'Reportes' },
  
  // Reservas
  { key: 'reservations.view', label: 'Ver Reservas', category: 'Reservas' },
  { key: 'reservations.create', label: 'Crear Reservas', category: 'Reservas' },
  { key: 'reservations.edit', label: 'Editar Reservas', category: 'Reservas' },
  { key: 'reservations.delete', label: 'Eliminar Reservas', category: 'Reservas' },
  { key: 'reservations.approve', label: 'Aprobar Reservas', category: 'Reservas' },
  
  // Configuración
  { key: 'settings.view', label: 'Ver Configuración', category: 'Configuración' },
  { key: 'settings.edit', label: 'Editar Configuración', category: 'Configuración' },
  { key: 'settings.users', label: 'Gestionar Usuarios', category: 'Configuración' },
  { key: 'settings.categories', label: 'Gestionar Categorías', category: 'Configuración' },
];

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const { showNotification } = useNotification();
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('categories'); // 'categories' o 'users'
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingCategoryMeta, setEditingCategoryMeta] = useState(null);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: '',
    is_active: true
  });
  const [editingUser, setEditingUser] = useState(null);
  const [showUserForm, setShowUserForm] = useState(false);
  const [userFormData, setUserFormData] = useState({
    nombre: '',
    email: '',
    password: '',
    category_id: null,
    is_active: true
  });
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [resetPasswordData, setResetPasswordData] = useState({ new_password: '' });
  const [changePasswordData, setChangePasswordData] = useState({ new_password: '', confirm_password: '' });

  // Verificar si el usuario actual es sysadmin
  const isSysadmin = useMemo(() => {
    if (!currentUser || !currentUser.category) {
      return false;
    }
    const categoryName = currentUser.category.name || currentUser.category.category_name;
    return categoryName && typeof categoryName === 'string' && categoryName.toLowerCase() === 'sysadmin';
  }, [currentUser]);

  // Cargar datos al montar
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [categoriesData, usersData] = await Promise.all([
        userCategoryService.getAll(),
        userManagementService.getAll()
      ]);
      setCategories(categoriesData || []);
      setUsers(usersData || []);
    } catch (error) {
      console.error('Error cargando datos:', error);
      showNotification(parseErrorMessage(error, 'Error al cargar los datos'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryEdit = (category) => {
    setEditingCategory(category);
  };

  const handleCategoryCreate = () => {
    setCategoryFormData({ name: '', description: '', is_active: true });
    setEditingCategoryMeta(null);
    setShowCategoryForm(true);
  };

  const handleCategoryMetaEdit = (category) => {
    setCategoryFormData({
      name: category.name || '',
      description: category.description || '',
      is_active: category.is_active !== false
    });
    setEditingCategoryMeta(category);
    setShowCategoryForm(true);
  };

  const handleCategoryMetaSave = async () => {
    if (!categoryFormData.name?.trim()) {
      showNotification('El nombre de la categoría es requerido', 'warning');
      return;
    }

    try {
      if (editingCategoryMeta?.id) {
        await userCategoryService.update(editingCategoryMeta.id, categoryFormData);
        showNotification('Categoría actualizada exitosamente', 'success');
      } else {
        await userCategoryService.create(categoryFormData);
        showNotification('Categoría creada exitosamente', 'success');
      }
      await loadData();
      setShowCategoryForm(false);
      setEditingCategoryMeta(null);
    } catch (error) {
      console.error('Error guardando categoría:', error);
      showNotification(parseErrorMessage(error, 'Error al guardar la categoría'), 'error');
    }
  };

  const handleCategoryDelete = async (categoryId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta categoría?')) {
      return;
    }
    try {
      await userCategoryService.delete(categoryId);
      await loadData();
      showNotification('Categoría eliminada exitosamente', 'success');
    } catch (error) {
      console.error('Error eliminando categoría:', error);
      showNotification(parseErrorMessage(error, 'Error al eliminar la categoría'), 'error');
    }
  };

  const handleCategorySave = async (categoryId, permissions) => {
    try {
      await userCategoryService.update(categoryId, { permissions });
      await loadData();
      setEditingCategory(null);
      showNotification('Permisos guardados exitosamente', 'success');
    } catch (error) {
      console.error('Error guardando permisos:', error);
      showNotification(parseErrorMessage(error, 'Error al guardar los permisos'), 'error');
    }
  };

  const handleUserCreate = () => {
    setUserFormData({
      nombre: '',
      email: '',
      password: '',
      category_id: null,
      is_active: true
    });
    setShowUserForm(true);
  };

  const handleUserEdit = (user) => {
    setUserFormData({
      nombre: user.nombre,
      email: user.email,
      password: '',
      category_id: user.category_id,
      is_active: user.is_active
    });
    setEditingUser(user);
    setShowUserForm(true);
  };

  const handleUserSave = async () => {
    // Validación básica
    if (!userFormData.nombre || !userFormData.email) {
      showNotification('Por favor completa todos los campos requeridos', 'warning');
      return;
    }

    if (!editingUser && !userFormData.password) {
      showNotification('La contraseña es requerida para crear un nuevo usuario', 'warning');
      return;
    }

    try {
      if (editingUser) {
        await userManagementService.update(editingUser.id, userFormData);
        showNotification('Usuario actualizado exitosamente', 'success');
      } else {
        await userManagementService.create(userFormData);
        showNotification('Usuario creado exitosamente', 'success');
      }
      await loadData();
      setShowUserForm(false);
      setEditingUser(null);
    } catch (error) {
      console.error('Error guardando usuario:', error);
      showNotification(parseErrorMessage(error, 'Error al guardar el usuario'), 'error');
    }
  };

  const handleUserDelete = async (userId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este usuario?')) {
      return;
    }
    try {
      await userManagementService.delete(userId);
      await loadData();
      showNotification('Usuario eliminado exitosamente', 'success');
    } catch (error) {
      console.error('Error eliminando usuario:', error);
      showNotification(parseErrorMessage(error, 'Error al eliminar el usuario'), 'error');
    }
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setResetPasswordData({ new_password: '' });
    setShowResetPasswordModal(true);
  };

  const handleConfirmResetPassword = async () => {
    if (!selectedUser) return;

    try {
      // Si no hay contraseña, enviar objeto vacío para usar la por defecto
      const passwordToSend = resetPasswordData.new_password?.trim() || null;
      const response = await userManagementService.resetPassword(selectedUser.id, passwordToSend);
      
      console.log('Respuesta de reset password:', response);
      
      const message = response?.detail || response?.message || `Contraseña reseteada exitosamente. Nueva contraseña: ${newPassword}`;
      showNotification(message, 'success');
      setShowResetPasswordModal(false);
      setSelectedUser(null);
      setResetPasswordData({ new_password: '' });
    } catch (error) {
      console.error('Error reseteando contraseña:', error);
      showNotification(parseErrorMessage(error, 'Error al resetear la contraseña'), 'error');
    }
  };

  const handleChangePassword = (user) => {
    setSelectedUser(user);
    setChangePasswordData({ new_password: '', confirm_password: '' });
    setShowChangePasswordModal(true);
  };

  const handleConfirmChangePassword = async () => {
    if (!selectedUser) return;

    // Validaciones
    if (!changePasswordData.new_password) {
      showNotification('La nueva contraseña es requerida', 'warning');
      return;
    }

    if (changePasswordData.new_password.length < 8) {
      showNotification('La contraseña debe tener al menos 8 caracteres', 'warning');
      return;
    }

    if (changePasswordData.new_password !== changePasswordData.confirm_password) {
      showNotification('Las contraseñas no coinciden', 'warning');
      return;
    }

    try {
      const response = await userManagementService.changePassword(selectedUser.id, changePasswordData.new_password);
      console.log('Respuesta de change password:', response);
      
      const message = response?.detail || response?.message || 'Contraseña cambiada exitosamente';
      showNotification(message, 'success');
      setShowChangePasswordModal(false);
      setSelectedUser(null);
      setChangePasswordData({ new_password: '', confirm_password: '' });
    } catch (error) {
      console.error('Error cambiando contraseña:', error);
      showNotification(parseErrorMessage(error, 'Error al cambiar la contraseña'), 'error');
    }
  };

  // Agrupar permisos por categoría
  const permissionsByCategory = PERMISSIONS_LIST.reduce((acc, perm) => {
    if (!acc[perm.category]) {
      acc[perm.category] = [];
    }
    acc[perm.category].push(perm);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex gap-4 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('categories')}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeTab === 'categories'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Shield className="w-5 h-5 inline mr-2" />
          Categorías y Permisos
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeTab === 'users'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Users className="w-5 h-5 inline mr-2" />
          Usuarios
        </button>
      </div>

      {/* Tab: Categorías y Permisos */}
      {activeTab === 'categories' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={handleCategoryCreate}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Crear Categoría
            </button>
          </div>

          <AnimatePresence>
            {showCategoryForm && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm"
              >
                <h3 className="text-xl font-bold text-gray-800 mb-4">
                  {editingCategoryMeta ? 'Editar Categoría' : 'Crear Categoría'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre
                    </label>
                    <input
                      type="text"
                      value={categoryFormData.name}
                      onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Descripción
                    </label>
                    <input
                      type="text"
                      value={categoryFormData.description}
                      onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={categoryFormData.is_active}
                      onChange={(e) => setCategoryFormData({ ...categoryFormData, is_active: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      Categoría activa
                    </label>
                  </div>
                </div>
                <div className="flex gap-3 justify-end mt-4">
                  <button
                    onClick={() => {
                      setShowCategoryForm(false);
                      setEditingCategoryMeta(null);
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleCategoryMetaSave}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Guardar
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {categories.map(category => (
            <div
              key={category.id}
              className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-800">{category.name}</h3>
                  {category.description && (
                    <p className="text-sm text-gray-600 mt-1">{category.description}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleCategoryMetaEdit(category)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors flex items-center gap-2"
                  >
                    <Edit className="w-4 h-4" />
                    Editar
                  </button>
                  <button
                    onClick={() => handleCategoryEdit(category)}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                  >
                    <Shield className="w-4 h-4" />
                    {editingCategory?.id === category.id ? 'Editando...' : 'Permisos'}
                  </button>
                  <button
                    onClick={() => handleCategoryDelete(category.id)}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Eliminar
                  </button>
                </div>
              </div>

              {editingCategory?.id === category.id ? (
                <div className="space-y-4">
                  {Object.entries(permissionsByCategory).map(([categoryName, perms]) => (
                    <div key={categoryName} className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-700 mb-3">{categoryName}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {perms.map(perm => {
                          const currentPermissions = editingCategory.permissions || {};
                          const isChecked = currentPermissions[perm.key] === true;
                          
                          return (
                            <label
                              key={perm.key}
                              className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  const newPermissions = {
                                    ...currentPermissions,
                                    [perm.key]: e.target.checked
                                  };
                                  setEditingCategory({
                                    ...editingCategory,
                                    permissions: newPermissions
                                  });
                                }}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">{perm.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  
                  <div className="flex gap-3 justify-end mt-4">
                    <button
                      onClick={() => setEditingCategory(null)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={() => handleCategorySave(editingCategory.id, editingCategory.permissions)}
                      className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      Guardar Permisos
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-500">
                  {category.permissions 
                    ? `${Object.values(category.permissions).filter(v => v === true).length} permisos activos`
                    : 'Sin permisos configurados'}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Tab: Usuarios */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={handleUserCreate}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
            >
              <UserPlus className="w-4 h-4" />
              Crear Usuario
            </button>
          </div>

          {/* Formulario de usuario */}
          <AnimatePresence>
            {showUserForm && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm"
              >
                <h3 className="text-xl font-bold text-gray-800 mb-4">
                  {editingUser ? 'Editar Usuario' : 'Crear Usuario'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre
                    </label>
                    <input
                      type="text"
                      value={userFormData.nombre}
                      onChange={(e) => setUserFormData({ ...userFormData, nombre: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={userFormData.email}
                      onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {editingUser ? 'Nueva Contraseña (dejar vacío para no cambiar)' : 'Contraseña'}
                    </label>
                    <input
                      type="password"
                      value={userFormData.password}
                      onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required={!editingUser}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Categoría
                    </label>
                    <select
                      value={userFormData.category_id || ''}
                      onChange={(e) => setUserFormData({ ...userFormData, category_id: e.target.value ? parseInt(e.target.value) : null })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Sin categoría</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={userFormData.is_active}
                      onChange={(e) => setUserFormData({ ...userFormData, is_active: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      Usuario activo
                    </label>
                  </div>
                </div>
                <div className="flex gap-3 justify-end mt-4">
                  <button
                    onClick={() => {
                      setShowUserForm(false);
                      setEditingUser(null);
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleUserSave}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Guardar
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Lista de usuarios */}
          <div className="space-y-3">
            {users.map(user => (
              <div
                key={user.id}
                className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h4 className="font-semibold text-gray-800">{user.nombre}</h4>
                      <span className={`px-2 py-1 rounded text-xs ${
                        user.is_active 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {user.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{user.email}</p>
                    {user.category && (
                      <p className="text-xs text-gray-500 mt-1">
                        Categoría: {user.category.name}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {isSysadmin && (
                      <>
                        <button
                          onClick={() => handleResetPassword(user)}
                          className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                          title="Resetear Contraseña"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleChangePassword(user)}
                          className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                          title="Cambiar Contraseña"
                        >
                          <Key className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleUserEdit(user)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Editar"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleUserDelete(user.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal Resetear Contraseña */}
      <AnimatePresence>
        {showResetPasswordModal && selectedUser && (
          <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 w-full"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <RotateCcw className="w-6 h-6 text-orange-600" />
                  Resetear Contraseña
                </h2>
                <button
                  onClick={() => {
                    setShowResetPasswordModal(false);
                    setSelectedUser(null);
                    setResetPasswordData({ new_password: '' });
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <p className="text-gray-600">
                  Usuario: <span className="font-semibold">{selectedUser.nombre} ({selectedUser.email})</span>
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nueva Contraseña (opcional)
                  </label>
                  <input
                    type="password"
                    value={resetPasswordData.new_password}
                    onChange={(e) => setResetPasswordData({ new_password: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                    placeholder="Dejar vacío para usar 'admin123'"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Si dejas vacío, se usará la contraseña por defecto: <code className="bg-gray-100 px-1 rounded">admin123</code>
                  </p>
                </div>
              </div>

              <div className="flex gap-3 justify-end mt-6">
                <button
                  onClick={() => {
                    setShowResetPasswordModal(false);
                    setSelectedUser(null);
                    setResetPasswordData({ new_password: '' });
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmResetPassword}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  Resetear
                </button>
              </div>
            </motion.div>
        )}
      </AnimatePresence>

      {/* Panel Cambiar Contraseña */}
      <AnimatePresence>
        {showChangePasswordModal && selectedUser && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 w-full"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Key className="w-6 h-6 text-purple-600" />
                  Cambiar Contraseña
                </h2>
                <button
                  onClick={() => {
                    setShowChangePasswordModal(false);
                    setSelectedUser(null);
                    setChangePasswordData({ new_password: '', confirm_password: '' });
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <p className="text-gray-600">
                  Usuario: <span className="font-semibold">{selectedUser.nombre} ({selectedUser.email})</span>
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nueva Contraseña
                  </label>
                  <input
                    type="password"
                    value={changePasswordData.new_password}
                    onChange={(e) => setChangePasswordData({ ...changePasswordData, new_password: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                    placeholder="Mínimo 8 caracteres"
                    required
                    minLength={8}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmar Contraseña
                  </label>
                  <input
                    type="password"
                    value={changePasswordData.confirm_password}
                    onChange={(e) => setChangePasswordData({ ...changePasswordData, confirm_password: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                    placeholder="Repite la contraseña"
                    required
                    minLength={8}
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end mt-6">
                <button
                  onClick={() => {
                    setShowChangePasswordModal(false);
                    setSelectedUser(null);
                    setChangePasswordData({ new_password: '', confirm_password: '' });
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmChangePassword}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                >
                  <Key className="w-4 h-4" />
                  Cambiar
                </button>
              </div>
            </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default UserManagement;

